import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Spinner } from 'react-bootstrap';
import PropTypes from 'prop-types';
import useToken from 'hooks/useToken';
import { useToast } from 'context/providers/ToastProvider';

const ModalAsociarPlanFacturacion = ({
    show,
    onHide,
    ordenSeleccionada,
    onPlanAsociado,
    idCliente
}) => {
    const { token, setToken } = useToken();
    const { showErrorToast, showSuccessToast } = useToast();
    
    const [planes, setPlanes] = useState([]);
    const [planParaAsociar, setPlanParaAsociar] = useState('');
    const [asociandoPlan, setAsociandoPlan] = useState(false);
    const [cargandoPlanes, setCargandoPlanes] = useState(false);

    useEffect(() => {
        if (show && idCliente) {
            obtenerPlanesFacturacion();
        }
    }, [show, idCliente]);

    const obtenerPlanesFacturacion = async () => {
        setCargandoPlanes(true);
        setPlanes([]);

        try {
            const response = await fetch('/api/cliente/getPlanesFacturacion', {
                method: 'POST',
                headers: {
                    'Authorization': 'Bearer ' + token,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ idClienteAkasia: idCliente })
            });

            if (response.ok) {
                const data = await response.json();

                if (data?.iResponseType === 1 && data?.detalles) {
                    const detalles = JSON.parse(data.detalles);
                    const table = detalles?.table2 || detalles?.Table2 || [];
                    setPlanes(Array.isArray(table) ? table : []);
                } else {
                    setPlanes(Array.isArray(data) ? data : data?.table1 || []);
                }
            } else {
                showErrorToast(`Error: ${response.status} ${response.statusText}`);
                
                if (response.status === 401) {
                    setToken(null);
                    location.href = "";
                }
            }
        } catch (error) {
            showErrorToast('Error al obtener planes de facturación: ' + error.message);
            console.log('Hubo un problema con la petición Fetch:', error.message);
        } finally {
            setCargandoPlanes(false);
        }
    };

    const handleAsociarPlan = async () => {
        if (!planParaAsociar) {
            showErrorToast('Seleccione un plan de facturación');
            return;
        }

        setAsociandoPlan(true);
        try {
            const response = await fetch('/api/orden/AsociarPlanFacturacion', {
                method: 'POST',
                headers: {
                    'Authorization': 'Bearer ' + token,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    idOrdenDetalle: ordenSeleccionada.idOrdenDetalle,
                    idPlanFacturaMx: parseInt(planParaAsociar)
                })
            });

            if (response.ok) {
                const data = await response.json();
                
                if (data.iResponseType === 1) {
                    showSuccessToast('Plan de facturación asociado correctamente');
                    
                    if (onPlanAsociado) {
                        onPlanAsociado();
                    }
                    
                    handleCerrarModal();
                } else {
                    showErrorToast(data.vMensaje || 'Error al asociar el plan');
                }
            } else {
                showErrorToast(`Error: ${response.status} ${response.statusText}`);
                
                if (response.status === 401) {
                    setToken(null);
                    location.href = "";
                }
            }
        } catch (error) {
            showErrorToast('Error al asociar el plan: ' + error.message);
            console.log('Hubo un problema con la petición Fetch:', error.message);
        } finally {
            setAsociandoPlan(false);
        }
    };

    const handleCerrarModal = () => {
        setPlanParaAsociar('');
        onHide();
    };

    return (
        <Modal show={show} centered onHide={handleCerrarModal}>
            <Modal.Header closeButton>
                <Modal.Title>Asociar a Plan de Facturación</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {ordenSeleccionada && (
                    <div className="mb-3">
                        <p><strong>Orden Detalle ID:</strong> {ordenSeleccionada.idOrdenDetalle}</p>
                    </div>
                )}
                
                {cargandoPlanes ? (
                    <div className="text-center py-3">
                        <Spinner animation="border" size="sm" className="me-2" />
                        <span>Cargando planes...</span>
                    </div>
                ) : (
                    <Form.Group>
                        <Form.Label>Seleccione un Plan de Facturación</Form.Label>
                        <Form.Select 
                            value={planParaAsociar}
                            onChange={(e) => setPlanParaAsociar(e.target.value)}
                        >
                            <option value="">Seleccione un plan...</option>
                            {planes.map((plan, index) => (
                                <option 
                                    key={plan.idPlanFacturaMx || index} 
                                    value={plan.idPlanFacturaMx}
                                >
                                    {plan.vRfc} - ID: {plan.idPlanFacturaMx}
                                </option>
                            ))}
                        </Form.Select>
                        {planes.length === 0 && (
                            <Form.Text className="text-muted">
                                No hay planes de facturación disponibles para este cliente.
                            </Form.Text>
                        )}
                    </Form.Group>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={handleCerrarModal}>
                    Cancelar
                </Button>
                <Button 
                    variant="primary" 
                    onClick={handleAsociarPlan}
                    disabled={asociandoPlan || !planParaAsociar || cargandoPlanes}
                >
                    {asociandoPlan ? (
                        <>
                            <Spinner animation="border" size="sm" className="me-2" />
                            Asociando...
                        </>
                    ) : (
                        'Asociar Plan'
                    )}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

ModalAsociarPlanFacturacion.propTypes = {
    show: PropTypes.bool.isRequired,
    onHide: PropTypes.func.isRequired,
    ordenSeleccionada: PropTypes.object,
    onPlanAsociado: PropTypes.func,
    idCliente: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired
};

export default ModalAsociarPlanFacturacion;
